import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../LanguageContext';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, changeLanguage, t } = useLanguage();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (isHomePage) {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setIsMobileMenuOpen(false);
      }
    } else {
      // Navigate to home page first, then scroll
      window.location.href = `/#${id}`;
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-32 md:h-36">
          {/* Logo */}
          <div className="flex items-center">
            <img
              src="/smartlogis-logo.png"
              alt="SmartLogis Logo"
              className="h-16 sm:h-20 md:h-24 lg:h-28 w-auto max-w-[200px] sm:max-w-[240px] md:max-w-[280px] lg:max-w-[320px] object-contain"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('accueil')}
              className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
            >
              {t('home')}
            </button>
            <button
              onClick={() => scrollToSection('produits')}
              className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
            >
              {t('products')}
            </button>
            <Link
              to="/configurator"
              className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
            >
              Configurateur
            </Link>
            <button
              onClick={() => scrollToSection('services')}
              className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
            >
              {t('services')}
            </button>
            <button
              onClick={() => scrollToSection('reservation')}
              className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
            >
              {t('reservation')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
            >
              {t('contact')}
            </button>
          </nav>

          {/* Contact Info, Language Selector & CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Language Selector */}
            <div className="flex items-center space-x-1 border-r border-gray-300 pr-4">
              <Globe className="w-4 h-4 text-gray-600" />
              <button
                onClick={() => changeLanguage('fr')}
                className={`px-2 py-1 text-sm font-medium transition-colors ${
                  language === 'fr' ? 'text-blue-600' : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                FR
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => changeLanguage('en')}
                className={`px-2 py-1 text-sm font-medium transition-colors ${
                  language === 'en' ? 'text-blue-600' : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                EN
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => changeLanguage('es')}
                className={`px-2 py-1 text-sm font-medium transition-colors ${
                  language === 'es' ? 'text-blue-600' : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                ES
              </button>
            </div>
            
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Phone className="w-4 h-4" />
              <span>514 464 3012</span>
            </div>
            <Button
              onClick={() => scrollToSection('reservation')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {t('reserve')}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-gray-700 hover:text-blue-600 transition-colors"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200 py-4 space-y-4">
            <button
              onClick={() => scrollToSection('accueil')}
              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
            >
              {t('home')}
            </button>
            <button
              onClick={() => scrollToSection('produits')}
              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
            >
              {t('products')}
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
            >
              {t('services')}
            </button>
            <button
              onClick={() => scrollToSection('reservation')}
              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
            >
              {t('reservation')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
            >
              {t('contact')}
            </button>
            
            {/* Language Selector Mobile */}
            <div className="px-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-2 flex items-center">
                <Globe className="w-4 h-4 mr-2" />
                Langue / Language / Idioma
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => changeLanguage('fr')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    language === 'fr' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Français
                </button>
                <button
                  onClick={() => changeLanguage('en')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    language === 'en' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  English
                </button>
                <button
                  onClick={() => changeLanguage('es')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    language === 'es' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Español
                </button>
              </div>
            </div>
            
            <div className="px-4 pt-4 border-t border-gray-200">
              <div className="flex items-center space-x-2 text-sm text-gray-600 mb-3">
                <Phone className="w-4 h-4" />
                <span>514 464 3012</span>
              </div>
              <Button
                onClick={() => scrollToSection('reservation')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                {t('reserve')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
